"use client";

import "../../utils/common/charts";
import "./page.css";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Container from "@/components/common/container";
import CommunicationMetrics from "@/components/dashboard/CommunicationMetrics";
import DashboardSkeleton from "@/components/dashboard/DashboardSkeleton";
import DashboardTour from "@/components/dashboard/DashboardTour";
import DashboardTourProvider from "@/components/dashboard/DashboardTourProvider";
import EmptyStateDashboard from "@/components/dashboard/EmptyStateDashboard";
import JumpInWithGailSection from "@/components/dashboard/JumpInWithGailSection";
import RecommendedActionsPanel from "@/components/dashboard/RecommendedActionsPanel";
import ResetOnboardingDialog from "@/components/dashboard/ResetOnboardingDialog";
import { ProductFocus } from "@/constants/onboarding";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useOnboardingModal } from "@/contexts/onboarding/OnboardingModalContext";
import { GailProductRoles, Permissions, RoleData } from "@/enums/user";
import { useConfigurationStatus } from "@/hooks/useConfigurationStatus";
import { useOrg } from "@/hooks/useOrg";
import { usePermissions } from "@/hooks/usePermissions";
import { GetLoginLocation } from "@/utils/common/login";
import { SetupAction, getRecommendedSetupActions } from "@/utils/dashboard/setupActions";
import {
  shouldAutoOpenOnboarding,
  shouldRedirectToTrialCheckout,
  shouldShowEmptyState,
  shouldWaitForDashboardConfig,
} from "@/utils/onboarding/autoOpen";
import { POST_ACCOUNT_CREATE_RETURN_TO } from "@/utils/onboarding/common";
import { useRouter } from "@bprogress/next";

export default function Home() {
  const router = useRouter();
  const { user } = useAuthContext();
  const { org, role, productAccess, productRolesState } = useOrg();
  const { canAccess } = usePermissions();
  const { openModal } = useOnboardingModal();
  const configStatus = useConfigurationStatus();

  const { t } = useTranslation("stats");

  const [viewDashboardOverride, setViewDashboardOverride] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const hasCompletedOnboarding = org?.details?.onboarding?.status === "completed";
  const hasAgentAccess =
    productRolesState.roles?.includes(GailProductRoles.GailAgentUser) ?? false;
  const shouldResumeTrialCheckout = shouldRedirectToTrialCheckout({
    productAccessLoading: productAccess.loading,
    orgHasActiveProducts: productAccess.orgHasActiveProducts,
    orgHasAnyProducts: (org?.products?.length ?? 0) > 0,
    userHasAnyProductAccess: productAccess.userHasAnyProductAccess,
    canStartCheckout: canAccess(RoleData.Settings, Permissions.Update),
    onboardingStatus: org?.details?.onboarding?.status,
  });
  const waitingForConfig = shouldWaitForDashboardConfig({
    hasCompletedOnboarding,
    configStatus,
    hasAgentAccess,
    productAccessLoading: productAccess.loading,
  });

  // Get product focus from org onboarding
  const productFocus = useMemo<ProductFocus[]>(() => {
    const gailFocus = org?.details?.onboarding?.gailFocus ?? [];
    return gailFocus.filter((f): f is ProductFocus =>
      ["inbound", "outbound", "gailgpt"].includes(f)
    );
  }, [org?.details?.onboarding?.gailFocus]);

  // Get recommended setup actions
  const setupActions = useMemo<SetupAction[]>(() => {
    if (!org || !configStatus || productFocus.length === 0) {
      return [];
    }
    return getRecommendedSetupActions(org, productFocus, configStatus);
  }, [org, productFocus, configStatus]);

  // No skeleton for recommended actions: the section mounts only once
  // detection finishes with pending actions, then animates in.
  const showRecommendedActions = setupActions.length > 0;

  // Single source of truth for the empty-state decision (shared with auto-open logic)
  const showEmptyState = shouldShowEmptyState({
    onboardingStatus: org?.details?.onboarding?.status,
    configStatus,
    viewDashboardOverride,
    orgCreatedAt: org?.createdAt,
    gailFocus: org?.details?.onboarding?.gailFocus,
  });

  const showDashboard = !showEmptyState;

  // Auto-open onboarding modal once per session, gated on config detection, role, and product access
  const autoOpenParams = {
    configStatus,
    onboardingStatus: org?.details?.onboarding?.status,
    viewDashboardOverride,
    role,
    productAccessLoading: productAccess.loading,
    gailAgentEnabled: productAccess.gailAgentEnabled,
    orgCreatedAt: org?.createdAt,
    gailFocus: org?.details?.onboarding?.gailFocus,
  };
  const canAutoOpenOnboarding = shouldAutoOpenOnboarding(autoOpenParams);

  const autoOpenAttemptedRef = useRef(false);
  useEffect(() => {
    if (canAutoOpenOnboarding && !autoOpenAttemptedRef.current) {
      openModal();
      autoOpenAttemptedRef.current = true;
    }
  }, [canAutoOpenOnboarding, openModal]);

  useEffect(() => {
    const location = GetLoginLocation();

    if (location) {
      router.push(location);
    }
  }, [router, user]);

  useEffect(() => {
    if (shouldResumeTrialCheckout) {
      router.replace(POST_ACCOUNT_CREATE_RETURN_TO);
    }
  }, [shouldResumeTrialCheckout, router]);

  useEffect(() => {
    document.title = `Gail | ${t("dashboard")}`;
  }, [t]);

  // Show dashboard skeleton while config detection is pending, or while we
  // send an unprovisioned trial org to /get-gail.
  if (shouldResumeTrialCheckout || waitingForConfig) {
    return (
      <Container title={t("dashboard")} subTitle={t("dashboardSubtitle")}>
        <div className="@container">
          <DashboardSkeleton />
        </div>
      </Container>
    );
  }

  const isDevOrStaging =
    process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_ENVIRONMENT === "staging";

  if (showDashboard) {
    return (
      <DashboardTourProvider showRecommendedActions={showRecommendedActions}>
        <DashboardTour onboardingCompleted={hasCompletedOnboarding} />
        <Container
          title={t("dashboard")}
          subTitle={t("dashboardSubtitle")}
          tourViewportId="dashboard-viewport"
        >
          {/* `@container` + `@xl:` variants: the layout responds to the width
              this page actually gets (e.g. squeezed beside the assistant
              dock), not the window width. */}
          <div className="@container">
            {/* The right rail keeps a 0px track while empty so switching to
                350px animates the column open instead of jumping the layout. */}
            <div
              className={clsx(
                "grid",
                "grid-cols-1",
                "gap-8",
                "items-start",
                "transition-[grid-template-columns,column-gap]",
                "duration-500",
                "ease-in-out",
                showRecommendedActions
                  ? "@xl:grid-cols-[1fr_350px]"
                  : "@xl:grid-cols-[1fr_0px] @xl:gap-x-0"
              )}
            >
              {/* Every child is placed explicitly: with the right rail
                  unmounted, auto-placement would otherwise flow the Jump In
                  sections into the empty second column. */}
              <div id="tour-dashboard-metrics" className="@xl:row-start-1 @xl:col-start-1">
                <CommunicationMetrics />
              </div>
              {showRecommendedActions && (
                <div
                  id="tour-recommended-actions"
                  className="@xl:row-start-1 @xl:col-start-2 @xl:row-span-2 @xl:overflow-hidden"
                >
                  <motion.div
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut", delay: 0.15 }}
                    className="@xl:w-[350px]"
                  >
                    <RecommendedActionsPanel setupActions={setupActions} />
                  </motion.div>
                </div>
              )}
              <div className="space-y-8 @xl:row-start-2 @xl:col-start-1">
                <JumpInWithGailSection variant="agent" />
                <JumpInWithGailSection variant="gpt" />
              </div>
            </div>
            {isDevOrStaging && (
              <>
                <div
                  className={clsx(
                    "flex",
                    "justify-end",
                    "mt-8",
                    "pt-4",
                    "border-t",
                    "border-neutral"
                  )}
                >
                  <button
                    onClick={() => setResetDialogOpen(true)}
                    className={clsx(
                      "text-xs",
                      "text-500",
                      "hover:text-700",
                      "transition-colors",
                      "underline",
                      "underline-offset-2"
                    )}
                  >
                    {t("resetOnboarding.buttonLabel")}
                  </button>
                </div>
                <ResetOnboardingDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen} />
              </>
            )}
          </div>
        </Container>
      </DashboardTourProvider>
    );
  }

  return (
    <Container title={t("dashboard")} subTitle={t("dashboardSubtitle")}>
      <>
        <EmptyStateDashboard
          onViewDashboard={isDevOrStaging ? () => setViewDashboardOverride(true) : undefined}
          onResetOnboarding={isDevOrStaging ? () => setResetDialogOpen(true) : undefined}
        />
        {isDevOrStaging && (
          <ResetOnboardingDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen} />
        )}
      </>
    </Container>
  );
}
